/**
 * TOTP Setup Gate — blocks app access until 2FA is configured.
 * Shown after registration or when the server rejects requests with totp_setup_required.
 */
import { useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Shield, Lock } from "lucide-react";

interface TotpSetupGateProps {
  onComplete: () => void;
}

export function TotpSetupGate({ onComplete }: TotpSetupGateProps) {
  const [step, setStep] = useState<"intro" | "setup" | "verify">("intro");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.post<{ qrCode: string; secret: string; uri: string }>(
        "/auth/totp/setup",
      );
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep("verify");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Setup");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;

    setLoading(true);
    setError("");
    try {
      await api.post("/auth/totp/verify", { totpCode });
      onComplete();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Ungültiger Code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      background: "var(--color-bg-primary)",
      padding: "20px",
    }}>
      <div style={{
        maxWidth: "440px",
        width: "100%",
        background: "var(--color-bg-secondary)",
        borderRadius: "16px",
        padding: "32px",
        border: "1px solid var(--color-border)",
      }}>
        {step === "intro" && (
          <>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <Shield size={48} style={{ color: "var(--color-accent)", marginBottom: "12px" }} />
              <h2 style={{ color: "var(--color-text-primary)", margin: "0 0 8px", fontSize: "22px" }}>
                Konto absichern
              </h2>
              <p style={{ color: "var(--color-text-secondary)", margin: 0, fontSize: "14px", lineHeight: 1.5 }}>
                Für die Nutzung von KollegePlus ist eine Zwei-Faktor-Authentifizierung (2FA) erforderlich.
                Das schützt dein Konto vor unbefugtem Zugriff.
              </p>
            </div>

            <div style={{
              background: "var(--color-bg-primary)",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "24px",
              fontSize: "13px",
              color: "var(--color-text-secondary)",
              lineHeight: 1.6,
            }}>
              <strong style={{ color: "var(--color-text-primary)" }}>Du brauchst eine Authenticator-App:</strong>
              <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
                <li>Authy</li>
              </ul>
            </div>

            <Button onClick={handleSetup} loading={loading} style={{ width: "100%" }}>
              <Lock size={16} style={{ marginRight: "8px" }} />
              2FA jetzt einrichten
            </Button>

            {error && (
              <p style={{ color: "var(--color-error)", fontSize: "13px", marginTop: "12px", textAlign: "center" }}>
                {error}
              </p>
            )}
          </>
        )}

        {step === "verify" && (
          <>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <h2 style={{ color: "var(--color-text-primary)", margin: "0 0 8px", fontSize: "22px" }}>
                QR-Code scannen
              </h2>
              <p style={{ color: "var(--color-text-secondary)", margin: 0, fontSize: "14px" }}>
                Scanne den QR-Code mit deiner Authenticator-App und gib den 6-stelligen Code ein.
              </p>
            </div>

            {qrCode && (
              <div style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "16px",
                background: "#ffffff",
                borderRadius: "12px",
                padding: "16px",
              }}>
                <img src={qrCode} alt="TOTP QR-Code" style={{ width: "200px", height: "200px" }} />
              </div>
            )}

            <details style={{ marginBottom: "16px", fontSize: "13px", color: "var(--color-text-secondary)" }}>
              <summary style={{ cursor: "pointer" }}>Manueller Code</summary>
              <code style={{
                display: "block",
                marginTop: "8px",
                padding: "8px 12px",
                background: "var(--color-bg-primary)",
                borderRadius: "6px",
                wordBreak: "break-all",
                fontSize: "12px",
                color: "var(--color-text-primary)",
              }}>
                {secret}
              </code>
            </details>

            <form onSubmit={handleVerify}>
              <Input
                label="6-stelliger Code"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                autoComplete="one-time-code"
              />
              {error && (
                <p style={{ color: "var(--color-error)", fontSize: "13px", marginTop: "8px" }}>
                  {error}
                </p>
              )}
              <Button
                type="submit"
                loading={loading}
                disabled={totpCode.length !== 6}
                style={{ width: "100%", marginTop: "16px" }}
              >
                Bestätigen & weiter
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
